import { parseTitleAttributes } from "@/lib/productTitle";
import type { FacetProduct } from "@/lib/shopify";

/** What a filter reads from a product: a full card, or the slim facet selection. */
type FilterableProduct = FacetProduct & { node: { description?: string } };

// Extract material keywords from product description/title
const MATERIAL_KEYWORDS = [
  "wood", "solid wood", "oak", "walnut", "pine", "birch", "teak", "mahogany", "bamboo", "acacia",
  "metal", "iron", "steel", "stainless steel", "aluminum", "brass", "copper", "chrome",
  "fabric", "linen", "cotton", "velvet", "polyester", "microfiber", "leather", "faux leather",
  "glass", "tempered glass",
  "marble", "stone", "concrete", "ceramic", "porcelain",
  "rattan", "wicker", "woven", "cane",
  "plastic", "resin", "acrylic",
  "foam", "memory foam",
];

// Extract style/type keywords
const STYLE_KEYWORDS = [
  "modern", "contemporary", "mid-century", "traditional", "rustic", "industrial",
  "farmhouse", "bohemian", "minimalist", "scandinavian", "coastal", "glam",
  "transitional", "vintage", "art deco",
];

// Extract color from title/description
const COLOR_KEYWORDS = [
  "black", "white", "gray", "grey", "brown", "beige", "cream", "ivory", "tan",
  "blue", "navy", "teal", "green", "olive", "sage",
  "red", "burgundy", "orange", "coral", "pink",
  "yellow", "gold", "silver", "copper", "bronze",
  "walnut", "oak", "espresso", "natural", "charcoal",
];

// Map of keywords → inferred product type for products missing a productType
const PRODUCT_TYPE_INFERENCE: [string[], string][] = [
  [["sofa", "couch"], "Sofas"],
  [["sectional"], "Sectionals"],
  [["love seat", "loveseat"], "Love Seats"],
  [["accent chair", "armchair", "arm chair", "club chair"], "Accent Chairs"],
  [["recliner"], "Recliners"],
  [["ottoman", "pouf", "footrest"], "Ottomans"],
  [["rocking chair", "rocker"], "Rocking Chairs"],
  [["coffee table"], "Coffee Tables"],
  [["console table", "entry table", "entryway table"], "Console Tables"],
  [["tv stand", "media console", "entertainment center"], "TV Stands"],
  [["bookcase", "bookshelf", "book shelf"], "Bookcases"],
  [["cabinet", "hutch", "curio"], "Cabinets"],
  [["sideboard", "buffet", "credenza"], "Sideboards"],
  [["dining table"], "Dining Tables"],
  [["dining chair", "parsons chair", "side chair"], "Dining Chairs"],
  [["dining set"], "Dining Sets"],
  [["bar stool", "bar chair", "counter stool", "counter chair"], "Bar Chairs"],
  [["bed frame", "platform bed", "panel bed", "canopy bed", "sleigh bed"], "Standard Beds"],
  [["bunk bed"], "Bunk Beds"],
  [["loft bed"], "Loft Beds"],
  [["dresser", "chest of drawers"], "Dressers"],
  [["headboard"], "Headboards"],
  [["bench", "entryway bench"], "Benches"],
  [["desk", "writing desk", "computer desk", "workstation"], "Desks"],
  [["office chair", "task chair", "executive chair"], "Office Chairs"],
  [["chandelier", "pendant light"], "Chandeliers"],
  [["floor lamp"], "Floor Lamps"],
  [["table lamp"], "Table Lamps"],
  [["area rug", "accent rug"], "Area Rugs"],
  [["mirror", "wall mirror"], "Mirrors"],
  [["fireplace", "electric fireplace"], "Fireplaces"],
  [["bathroom vanity", "bath vanity"], "Bathroom Vanities"],
];

// Static list of core furniture product types for use in generic category filters
const STATIC_PRODUCT_TYPES: [string, string][] = [
  ["Sofas", "Sofas"],
  ["Sectionals", "Sectionals"],
  ["Love Seats", "Love Seats"],
  ["Accent Chairs", "Accent Chairs"],
  ["Recliners", "Recliners"],
  ["Ottomans", "Ottomans"],
  ["Coffee Tables", "Coffee Tables"],
  ["Console Tables", "Console Tables"],
  ["TV Stands", "TV Stands"],
  ["Bookcases", "Bookcases"],
  ["Cabinets", "Cabinets"],
  ["Sideboards", "Sideboards"],
  ["Dining Tables", "Dining Tables"],
  ["Dining Chairs", "Dining Chairs"],
  ["Dining Sets", "Dining Sets"],
  ["Bar Chairs", "Bar & Counter Chairs"],
  ["Standard Beds", "Beds"],
  ["Bunk Beds", "Bunk Beds"],
  ["Loft Beds", "Loft Beds"],
  ["Dressers", "Dressers"],
  ["Headboards", "Headboards"],
  ["Benches", "Benches"],
  ["Desks", "Desks"],
  ["Office Chairs", "Office Chairs"],
  ["Chandeliers", "Chandeliers"],
  ["Floor Lamps", "Floor Lamps"],
  ["Table Lamps", "Table Lamps"],
  ["Area Rugs", "Area Rugs"],
  ["Mirrors", "Mirrors"],
  ["Fireplaces", "Fireplaces"],
  ["Bathroom Vanities", "Bathroom Vanities"],
];

/**
 * Returns static product type filter items for generic categories.
 * Each item is [displayLabel, shopifyProductType] — no counts shown.
 */
export function getStaticProductTypeFilters(): [string, number][] {
  return STATIC_PRODUCT_TYPES.map(([, label]) => [label, 0]);
}

/**
 * Infer a productType from title/description when Shopify doesn't provide one.
 */
export function inferProductType(title: string, description?: string): string | null {
  const text = `${title} ${description || ""}`.toLowerCase();
  for (const [keywords, type] of PRODUCT_TYPE_INFERENCE) {
    for (const kw of keywords) {
      if (text.includes(kw)) return type;
    }
  }
  return null;
}

/**
 * Get the Shopify query value for a static product type filter label.
 * e.g. "Beds" → "Standard Beds", "Bar & Counter Chairs" → "Bar Chairs"
 */
export function getShopifyProductTypeFromLabel(label: string): string | null {
  const match = STATIC_PRODUCT_TYPES.find(([, displayLabel]) => displayLabel === label);
  return match ? match[0] : null;
}

/**
 * Whole words only, as the server's `title:` term matches them. As a substring
 * "tan" was found in every nighTSTANd and "red" in upholsteRED: the search
 * Color filter offered "Tan (268)" and returned 437 results, none of them tan.
 */
const hasWord = (text: string, word: string) =>
  new RegExp(`(?:^|[^a-z0-9])${word.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(?![a-z0-9])`).test(text.toLowerCase());

function extractKeywords(text: string, keywords: string[]): string[] {
  const lower = text.toLowerCase();
  const found = new Set<string>();
  // Sort by length descending to match longer phrases first
  const sorted = [...keywords].sort((a, b) => b.length - a.length);
  for (const kw of sorted) {
    if (hasWord(lower, kw)) {
      // Don't add if a longer keyword containing this was already added
      let isSubstring = false;
      for (const existing of found) {
        if (existing.includes(kw) && existing !== kw) {
          isSubstring = true;
          break;
        }
      }
      if (!isSubstring) {
        found.add(kw);
      }
    }
  }
  return Array.from(found).map(k => k.split(" ").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" "));
}

export interface ExtractedFilters {
  productTypes: Map<string, number>;
  materials: Map<string, number>;
  styles: Map<string, number>;
  colors: Map<string, number>;
}

// Regex to detect raw dimension strings like '19" X 23" X 46"' or '23.5 X 19 X 42.5'
const DIMENSION_PATTERN = /^\d+(\.\d+)?\s*"?\s*[Xx×]\s*\d+/;

/**
 * `optionColors: false` leaves variant option values out of the colours. A
 * category resolves its colour filter on the server as a title/tag term, where
 * an option value ("Dark taupe", "Black,grey", "Heather") finds nothing.
 */
export function extractFiltersFromProducts(
  products: FilterableProduct[],
  { optionColors: withOptionColors = true }: { optionColors?: boolean } = {},
): ExtractedFilters {
  const productTypes = new Map<string, number>();
  const materials = new Map<string, number>();
  const styles = new Map<string, number>();
  const colors = new Map<string, number>();

  for (const p of products) {
    const { title, description, productType } = p.node;

    // Product type from Shopify — skip raw dimension strings; fall back to inference
    let pt = productType?.trim() || "";
    if (!pt || DIMENSION_PATTERN.test(pt)) {
      pt = inferProductType(title, description || "") || "";
    }
    if (pt) {
      productTypes.set(pt, (productTypes.get(pt) || 0) + 1);
    }

    // Materials and styles from the title only: the server-side facet matches
    // title (and tags), so a count taken from descriptions advertised options
    // like "Contemporary (91)" that then returned "No products match".
    for (const mat of extractKeywords(title, MATERIAL_KEYWORDS)) {
      materials.set(mat, (materials.get(mat) || 0) + 1);
    }

    // Styles
    for (const style of extractKeywords(title, STYLE_KEYWORDS)) {
      styles.set(style, (styles.get(style) || 0) + 1);
    }

    // Colors — from title only for accuracy, plus variant options
    const titleColors = extractKeywords(title, COLOR_KEYWORDS);
    const optionColors = (withOptionColors && p.node.options
      ?.filter(o => o.name.toLowerCase() === "color" || o.name.toLowerCase() === "colour")
      .flatMap(o => o.values)) || [];
    
    const allColors = new Set([...titleColors, ...optionColors.map(c => c.charAt(0).toUpperCase() + c.slice(1).toLowerCase())]);
    for (const color of allColors) {
      colors.set(color, (colors.get(color) || 0) + 1);
    }
  }

  return { productTypes, materials, styles, colors };
}

// Check if a product matches a filter value
export function productMatchesFilter(product: FilterableProduct, groupKey: string, values: string[]): boolean {
  if (values.length === 0) return true;

  const { title, description, productType } = product.node;
  // Material, style and colour match what extractFiltersFromProducts counted —
  // the title's own keywords — so the number beside an option is the number of
  // results it gives. Colour used to search the description as well.
  const named = (keywords: string[]) => {
    const found = extractKeywords(title, keywords).map((k) => k.toLowerCase());
    return values.some((v) => found.includes(v.toLowerCase()));
  };

  switch (groupKey) {
    case "productType": {
      // Check explicit productType or inferred type
      const effectiveType = productType || inferProductType(title, description || "") || "";
      return values.some(v => effectiveType.toLowerCase() === v.toLowerCase());
    }
    case "material":
      return named(MATERIAL_KEYWORDS);
    case "style":
      return named(STYLE_KEYWORDS);
    case "color": {
      const optionColors = product.node.options
        ?.filter(o => o.name.toLowerCase() === "color" || o.name.toLowerCase() === "colour")
        .flatMap(o => o.values.map(c => c.toLowerCase())) || [];
      return named(COLOR_KEYWORDS) || values.some(v => optionColors.includes(v.toLowerCase()));
    }
    case "configuration": {
      const attrs = parseTitleAttributes(title);
      return values.some(v => attrs.configs.some(c => c.toLowerCase() === v.toLowerCase()));
    }
    case "size": {
      const attrs = parseTitleAttributes(title);
      return values.some(v => (attrs.pieceCount || "").toLowerCase() === v.toLowerCase());
    }
    default:
      return true;
  }
}

// ─────────────────────────────────────────────────────────────
// Server-side facet queries
//
// Filtering client-side only ever saw the first page, so a large category
// filtered to one colour reported "No products match your filters" while
// hundreds matched server-side. These clauses are AND-ed into the Shopify
// `query` string instead. Verified against the live Storefront API (2026-09-12):
//   (product_type:"Sofas" AND available_for_sale:true AND variants.price:>0)
//     AND (title:"faux leather" OR tag:"faux leather")            → 17
//     AND ((title:gray OR tag:gray) OR (title:blue OR tag:blue))  → 50+
//     AND variants.price:>=500 AND variants.price:<=1500          → 33
// Never use the `a..b` range form — it silently returns 0.
const SERVER_FACET_KEYS = new Set(["productType", "material", "style", "color"]);

/** True when this filter group is resolved by Shopify rather than in code. */
export function isServerFacet(groupKey: string): boolean {
  return SERVER_FACET_KEYS.has(groupKey);
}

function term(value: string): string {
  const clean = value.trim().toLowerCase().replace(/["\\]/g, "");
  if (!clean) return "";
  const quoted = /\s/.test(clean) ? `"${clean}"` : clean;
  return `(title:${quoted} OR tag:${quoted})`;
}

/**
 * Build the Shopify clause for the selected facets and price range.
 * Returns `null` when nothing needs to be sent to the API.
 */
export function buildFacetQuery(
  selectedFilters: Record<string, string[]>,
  priceFilter: [number, number] | null
): string | null {
  const clauses: string[] = [];

  const types = selectedFilters.productType || [];
  if (types.length) {
    const typeClause = types
      .map((label) => `product_type:"${(getShopifyProductTypeFromLabel(label) || label).replace(/"/g, "")}"`)
      .join(" OR ");
    clauses.push(`(${typeClause})`);
  }

  for (const key of ["material", "style", "color"]) {
    const values = (selectedFilters[key] || []).map(term).filter(Boolean);
    if (values.length) clauses.push(`(${values.join(" OR ")})`);
  }

  if (priceFilter) {
    clauses.push(`variants.price:>=${priceFilter[0]}`);
    clauses.push(`variants.price:<=${priceFilter[1]}`);
  }

  return clauses.length ? clauses.join(" AND ") : null;
}

/** Map a UI sort option onto Shopify `sortKey` / `reverse`. */
export function mapSortToShopify(
  sort: string,
  fallbackSortKey?: string,
  fallbackReverse?: boolean
): { sortKey?: string; reverse?: boolean } | undefined {
  switch (sort) {
    case "price-asc":
      return { sortKey: "PRICE", reverse: false };
    case "price-desc":
      return { sortKey: "PRICE", reverse: true };
    case "name-asc":
      return { sortKey: "TITLE", reverse: false };
    case "name-desc":
      return { sortKey: "TITLE", reverse: true };
    case "newest":
      return { sortKey: "CREATED_AT", reverse: true };
    default:
      return fallbackSortKey ? { sortKey: fallbackSortKey, reverse: fallbackReverse } : undefined;
  }
}
